import type { CharacterKey } from '@secretario/shared';
import { useEffect, useRef } from 'react';
import { characterImage, type Expression } from '../../lib/characters.js';
import type { LiveMood, LivePhase } from '../../lib/voice/controller.js';
import type { VoiceSample } from '../../lib/voice/lipsync.js';

/**
 * Personagem em imagem (todos os que não são 3D): as poses que ele já tem se
 * alternam conforme a conversa, e o corpo respira e acompanha o volume da fala.
 */
export function expressionFor(phase: LivePhase, mood: LiveMood): Expression {
  if (phase === 'thinking' || mood === 'thinking') return 'elegante';
  if (phase === 'consulting') return 'direto';
  if (phase === 'hearing' || phase === 'listening') return 'tranquilo';
  if (mood === 'happy' || mood === 'proud') return 'divertido';
  if (mood === 'sheepish') return 'tranquilo';
  if (mood === 'concerned') return 'direto';
  if (mood === 'curious') return 'espontaneo';
  return 'simpatico';
}

export function LiveStage2D({
  characterKey,
  phase,
  mood,
  levels,
}: {
  characterKey: CharacterKey;
  phase: LivePhase;
  mood: LiveMood;
  levels: () => { input: number; output: number; voice?: VoiceSample | null };
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const levelsRef = useRef(levels);
  levelsRef.current = levels;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let frame = 0;
    let bounce = 0;
    let lean = 0;
    const started = performance.now();
    const draw = (now: number) => {
      const t = (now - started) / 1000;
      const { input, output } = levelsRef.current();
      bounce += (output - bounce) * 0.35;
      // Ouvindo: inclina um pouco na direção de quem fala.
      const leanTarget = phaseRef.current === 'hearing' ? Math.min(1, input * 2) : 0;
      lean += (leanTarget - lean) * 0.08;
      const breathe = Math.sin(t * 1.6) * 0.008;
      const node = bodyRef.current;
      if (node) {
        node.style.transform = `translateY(${(-bounce * 10).toFixed(2)}px) rotate(${(lean * -2.5).toFixed(2)}deg) scale(${(1 + breathe + bounce * 0.025).toFixed(4)}, ${(1 - breathe + bounce * 0.035).toFixed(4)})`;
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);

  const expression = expressionFor(phase, mood);
  return (
    <div className="lv-stage2d" aria-hidden="true">
      <div ref={bodyRef} className="lv-stage2d-body">
        <img key={expression} src={characterImage(characterKey, expression)} alt="" />
      </div>
    </div>
  );
}
